import { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import { Hand, Lock } from 'lucide-react';
import { usePointerCoarse } from './usePointerCoarse';

/**
 * Hand single-finger gestures back to the page.
 *
 * Leaflet puts `touch-action: none` on its own container whenever the drag and
 * touch-zoom handlers are both live — that is the
 * `.leaflet-touch-drag.leaflet-touch-zoom` rule in leaflet.css, and the classes
 * are added by the handlers themselves. On a phone that means a swipe starting
 * anywhere on the map pans the map and the page does not move. A map is often
 * most of a phone screen with the page's real content underneath it, so this
 * reads as a page that will not scroll — on the network map, tapping Live or
 * Scenarios looked like it did nothing at all.
 *
 * Locking disables those two handlers, which removes the classes and lets the
 * rule stop applying. Nothing here writes `touch-action` directly — competing
 * with Leaflet for that property is how this kind of fix rots.
 *
 * What a locked map keeps: taps. A marker still opens its popup, the zoom
 * buttons still zoom, a draggable marker still drags. Only the gestures that
 * were stealing the scroll are handed over, and the chip hands them straight
 * back.
 *
 * Drop it in as a child of any `MapContainer`:
 *
 *     <MapContainer …>
 *       …
 *       <MapTouchGate />
 *     </MapContainer>
 *
 * Pass `disabled` while the map is the work surface rather than a picture —
 * drawing a boundary, placing a node — where every gesture is wanted and the
 * control that started the job is right there.
 */
export default function MapTouchGate({ disabled = false }: { disabled?: boolean }) {
  const coarse = usePointerCoarse();
  const [unlocked, setUnlocked] = useState(false);
  const locked = coarse && !disabled && !unlocked;

  return (
    <>
      <MapGestureLock locked={locked} />
      {coarse && !disabled && (
        <MapLockChip locked={locked} onChange={(next) => setUnlocked(!next)} />
      )}
    </>
  );
}

/**
 * The mechanism, on its own: while `locked`, pan and pinch belong to the page.
 *
 * Separate from the chip so a map that owns its own lock state can use it, and
 * so the `touch-action` behaviour can be tested without a touchscreen.
 */
export function MapGestureLock({ locked }: { locked: boolean }) {
  const map = useMap();
  // What the map was born with. Restoring *that* rather than force-enabling
  // matters on a touch laptop, where `touchZoom` is off by default and turning
  // it on would put `touch-action: none` back on a map that never had it.
  const initial = useRef<{ drag: boolean; zoom: boolean } | null>(null);

  useEffect(() => {
    if (!initial.current) {
      initial.current = {
        drag: map.dragging?.enabled() ?? false,
        zoom: map.touchZoom?.enabled() ?? false,
      };
    }
    const restore = () => {
      if (initial.current?.drag) map.dragging?.enable();
      if (initial.current?.zoom) map.touchZoom?.enable();
    };

    if (locked) {
      map.dragging?.disable();
      map.touchZoom?.disable();
    } else {
      restore();
    }

    // Also runs when the lock unmounts — leaving a map locked with no control
    // to unlock it would be worse than the bug.
    return restore;
  }, [map, locked]);

  return null;
}

/** The control that swaps the two. Positioned against the Leaflet container. */
export function MapLockChip({
  locked, onChange,
}: {
  locked: boolean;
  onChange: (locked: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!locked)}
      // Above Leaflet's own control panes (1000), clear of the zoom buttons
      // top-left and the attribution bottom-right.
      className="leaflet-touch-gate absolute right-2 top-2 z-[1100] flex items-center gap-1.5
                 rounded-full bg-white/90 px-3 py-1.5 text-xs font-medium text-gray-700
                 shadow ring-1 ring-black/10 backdrop-blur"
      aria-label={locked
        ? 'Unlock the map to pan and zoom with your fingers'
        : 'Lock the map so the page scrolls'}
      aria-pressed={!locked}
    >
      {locked
        ? <><Hand className="w-3.5 h-3.5" /> Tap to move map</>
        : <><Lock className="w-3.5 h-3.5" /> Lock map</>}
    </button>
  );
}
